import * as React from 'react';
import { Button, Modal, Row, Col, Tabs, List, Badge } from 'antd';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { Prompt } from 'react-router';

import { DocumentTemplateCategoryEditorController } from 'src/app/general/document-template-categories/parts/document-template-category-editor.controller';
import { CardController } from 'src/app/shared/card.controller';
import { HeaderView } from 'src/app/shared/header.view';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { DataTableView } from 'src/core/ui/data-table.view';
import { TabLanguageController } from 'src/core/ui/language/tab-language.controller';
import { ToastNotifications } from 'src/core/ui/toast';
import { UploadView } from 'src/core/ui/upload.view';
import { ContextService } from 'src/core/services/context.service';
import { CustomTemplateItemDataService } from './parts/custom-template-list.data';
import { CustomTemplateFormController } from './parts/custom-template-form.controller';
import DatasetView from "./parts/dataset.view";
import { DocumentTemplateBrandController } from './brands/brand.controller';
import { DocumentTemplateBrandDataService } from "./brands/brand-form.data";
import { DocumentTemplateMarketController } from './markets/market.controller';
import { DocumentTemplateMarketDataService } from "./markets/market-form.data";
import { ZuluIcon } from 'src/app/shared/icon';

interface IDocumentTemplateFormViewProps extends ComponentViewProps<IDocumentTemplateItem> {
	title: string;
	model: IDocumentTemplateItem;
	save: () => Promise<void>;
	cancel: () => void;
	onFileChange: (file: File) => void;
	modalFileChanged: (customTemplate: any, file: File) => void;
	mode: ControlMode;
	customTemplates: DataSet<ICustomTemplateItem>;
	brands: DocumentTemplateBrandDataService;
	markets: DocumentTemplateMarketDataService;
	onModeChange: (mode: ControlMode) => void;
	setBusy(isBusy: boolean);
	contextService: ContextService;
	createNewItem: () => Promise<ICustomTemplateItem>;
	isAdmin: boolean;
	userId: number | null;
	documentServices: { label: string; value: string, inline: boolean }[];
	inlineTemplateDatasets: boolean;
	documentDatasets: DocumentDatasetSummary[];
	reportsDatasets: DocumentDatasetSummary[];
}

@observer
export class DocumentTemplateFormView extends React.Component<IDocumentTemplateFormViewProps, any> {
	constructor(props: IDocumentTemplateFormViewProps) {
		super(props);
	}

	@observable
	showCreateCustomModal = false;

	@observable
	openInModal: any = null;

	@observable
	modalMode: 'update' | 'new';

	@resolve
	toast: ToastNotifications;

	@autobind
	async launchCreateCustomModal(ev?: React.MouseEvent<HTMLButtonElement>) {
		if (ev != null) {
			ev.preventDefault();
		}

		this.props.setBusy(true);
		this.modalMode = 'new';

		this.openInModal = (this.props.customTemplates as CustomTemplateItemDataService)
			.add(this.props.userId);

		this.openInModal.templateId = this.props.model.id;

		this.showCreateCustomModal = true;

		this.props.setBusy(false);
	}

	@autobind
	async cancelCreateModal(ev?: React.MouseEvent<HTMLButtonElement>) {
		if (ev != null) {
			ev.preventDefault();
		}

		this.openInModal = undefined;
		this.showCreateCustomModal = false;
	}

	@autobind
	async createCustomTemplate(ev?: React.MouseEvent<HTMLButtonElement>) {
		if (ev != null) {
			ev.preventDefault();
		}

		// validating
		if (!this.openInModal.file) {
			this.toast.showToast('Validation', 'custom template must have a file', 'error');
			return;
		}
		if (this.modalMode == 'new' && this.props.customTemplates.items.filter(
			(value: ICustomTemplateItem, index, array) => value.userId == this.openInModal.userId
				&& value.languageCode == this.openInModal.languageCode).length > 0) {

			this.toast.showToast('Validation', 'only one configuration user/language template is permitted', 'error');
			return;
		}

		this.props.setBusy(true);

		if (this.modalMode == 'new') {
			await this.props.customTemplates.create(() => this.openInModal);
		}

		this.openInModal = undefined;
		this.showCreateCustomModal = false;

		this.props.setBusy(false)
	}

	@autobind
	rowKey(record: ICustomTemplateItem) {
		return record.userId.toString() + record.languageCode;
	}

	render() {
		return (
			<div className="window window-normal">
				<Prompt
					when={this.props.contextService.isDirty}
					message={location => `If you go to ${location.pathname}, all changes will be lost. Are you sure you want to continue?`}
				/>
				<Row>
					<Col xs={24} xl={{ offset: 3, span: 18 }}>
						<Row>
							<Col xs={24}>
								<HeaderView
									title={this.props.title}
									mode={this.props.mode}
									onModeChanged={this.props.onModeChange}
									save={this.props.save}
									cancel={this.props.cancel}
									contextService={this.props.contextService}
								/>
							</Col>
						</Row>
						<Row>
							<Col xs={24}>
								<div className="card p-3">
									{this.renderTabs()}
								</div>
							</Col>
						</Row>
					</Col>
				</Row>
			</div>
		);
	}

	private renderTabs() {
		const { documentDatasets, inlineTemplateDatasets, mode, model, brands, markets, setBusy } = this.props;

		return (
			<Tabs defaultActiveKey="General information">
				<Tabs.TabPane tab={<span><ZuluIcon type="book" />General information</span>} key="General information">
					{this.renderGeneral()}
				</Tabs.TabPane>
				<Tabs.TabPane
					tab={<span><ZuluIcon type="database" />Data Sets</span>}
					key="Data Sets"
					disabled={model.documentService == null || inlineTemplateDatasets}
				>
					<List
						itemLayout="horizontal"
						dataSource={documentDatasets}
						renderItem={this.renderDatasetItem}
					/>
				</Tabs.TabPane>
				<Tabs.TabPane
					tab={<Badge style={{ right: '-15px', marginTop: '5px' }} count={this.props.brands.count}><span><ZuluIcon type="trademark" />Brands</span></Badge>}
					key="Brands"
					disabled={!model.isDocument}
				>
					<DocumentTemplateBrandController
						model={model}
						dataService={brands}
						mode={mode}
						setBusy={setBusy}
					/>
				</Tabs.TabPane>
				<Tabs.TabPane
					tab={<Badge style={{ right: '-15px', marginTop: '5px' }} count={this.props.markets.count}><span><ZuluIcon type="wallet" />Markets</span></Badge>}
					key="Markets"
					disabled={!model.isDocument}
				>
					<DocumentTemplateMarketController
						model={model}
						dataService={markets}
						mode={mode}
						setBusy={setBusy}
					/>
				</Tabs.TabPane>
			</Tabs>
		);
	}

	private renderDatasetItem = (item: DocumentDatasetSummary) => {
		const { mode, model } = this.props;
		const datasets: { [dataset: string]: Array<string> } = model.dynamicProperties["datasets"];
		return <DatasetView datasets={datasets} item={item} mode={mode} onDatasetChange={this.onDatasetChange} />;
	}

	private renderGeneral() {
		const translations = [
			{
				title: 'Title',
				dynamicProperties: this.props.model.dynamicProperties['displayName'],
				required: true
			},
			{
				title: 'Description',
				dynamicProperties: this.props.model.dynamicProperties['translations'],
				rows: 3
			}
		];

		return (
			<CardController
				mode={this.props.mode}
				icon="book"
				className="card-borderless"
				title="General information">
				<Row gutter={16}>
					<Col xs={24} md={16}>
						<Row>
							<Col xs={24}>
								<TabLanguageController
									model={this.props.model}
									mode={this.props.isAdmin ? this.props.mode : 'display'}
									dynamicProperties={translations}
									setBusy={this.props.setBusy}
									isRichTextBox={false}
								/>
							</Col>
						</Row>
					</Col>
					<Col xs={24} md={8}>
						<Row>
							<Col xs={24}>
								<DocumentTemplateCategoryEditorController
									value={this.props.model.categoryId}
									mode={this.props.isAdmin ? this.props.mode : 'display'}
									onChange={(newValue) => {
										this.props.model.categoryId = newValue.id;
									}}
									label="Category"
									required={true}
									setBusy={this.props.setBusy}
								/>
							</Col>
							<Col xs={24}>
								{this.renderReportInfo()}
							</Col>
							<Col xs={24}>
								<UploadView
									value={this.props.model.dynamicProperties.fileName}
									mode={this.props.isAdmin ? this.props.mode : 'display'}
									onChange={this.props.onFileChange}
									label="File"
								/>

							</Col>
						</Row>

					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<Modal
							style={{ top: 20 }}
							width='80%'
							visible={this.showCreateCustomModal}
							onCancel={this.cancelCreateModal}
							title="Create custom template"
							maskClosable={false}
							footer={[
								<Button
									key="confirm"
									type="primary"
									onClick={this.createCustomTemplate}>
									OK
													</Button>
							]}>
							{
								this.openInModal &&
								<CustomTemplateFormController
									fileChanged={
										(f) => {
											this.props.modalFileChanged(this.openInModal, f);
										}
									}
									showUserCombo={this.props.isAdmin}
									setBusy={this.props.setBusy}
									mode={this.props.mode}
									templateName={'...'}
									model={this.openInModal} />
							}
						</Modal>
						<CardController
							title="Custom Templates"
							className="card-borderless"
							mode={this.props.mode}>
							<DataTableView
								rowKey={this.rowKey}
								model={this.props.customTemplates}
								createNewItem={
									() => {
										this.launchCreateCustomModal();
										return Promise.resolve();
									}
								}
								columns={this.props.isAdmin ? [
									{ title: 'User', key: 'userName', dataIndex: 'userName' },
									{ title: 'Language', key: 'languageCode', dataIndex: 'languageCode' }
								] : [
										{ title: 'Language', key: 'languageCode', dataIndex: 'languageCode' }
									]}
								itemDoubleClick={(item) => {
									this.modalMode = 'update';
									this.openInModal = item;
									this.showCreateCustomModal = true;
								}}
								setBusy={this.props.setBusy}
							/>
						</CardController>
					</Col>
				</Row>
			</CardController>
		);
	}

	@autobind
	private onDatasetChange(item: DocumentDatasetSummary, checkedValue: Array<string>) {
		const { model } = this.props;

		if (checkedValue.length > 0) {
			if (model.dynamicProperties["datasets"] == null) {
				const datasets = {};
				datasets[item.id] = checkedValue;
				model.dynamicProperties["datasets"] = datasets;
			} else {
				model.dynamicProperties["datasets"][item.id] = checkedValue;
			}
		} else {
			if (model.dynamicProperties["datasets"] != null && model.dynamicProperties["datasets"][item.id] != null) {
				delete model.dynamicProperties["datasets"][item.id];
			}
		}
		this.forceUpdate();
	}

	private renderReportInfo(): React.ReactNode {
		const { model } = this.props;

		return model.isDocument
			? (
				<DropdownView
					required={true}
					model={this.props.model.documentService}
					disabled={this.props.isAdmin ? this.props.mode == "display" : true}
					onChanged={(item) => this.props.model.documentService = item.value}
					label="Document type"
					options={this.props.documentServices}
				/>
			)
			: (
				<DropdownView
					required={true}
					model={this.props.model.dynamicProperties.reactComponent}
					disabled={this.props.isAdmin ? this.props.mode == "display" : true}
					onChanged={this.onReportDatasetChange}
					label="Report dataset"
					options={this.props.reportsDatasets}
					keyField="id"
					textField="name"
				/>
			);
	}

	private onReportDatasetChange = (item: DocumentDatasetSummary) => {
		this.props.model.dynamicProperties.reactComponent = item.id;
		this.props.model.documentService = this.props.documentServices.find(e => e.inline).value;
	}
}



// WEBPACK FOOTER //
// ./src/app/general/document-templates/form/document-template-form.view.tsx